import {
  IconAlertTriangle,
  IconChecks,
  IconHourglass,
} from "@tabler/icons-react"
import { BadgeVariant, StatusBadge } from "@tfg/ui"
import { useTranslation } from "react-i18next"

import {
  findIngestionsResponseDtoStatusEnum,
  FindIngestionsResponseDtoStatusEnumKey,
} from "~/gen/api"

export const IngestionStatusVariantEnum = {
  PENDING: BadgeVariant.WARNING,
  PRODUCTS_PRE_VERIFIED: BadgeVariant.INFO,
  ERROR: BadgeVariant.ERROR,
  FAILED: BadgeVariant.ERROR,
  BLANK_PRODUCTS_VERIFIED: BadgeVariant.SUCCESS,
  BLANK_PRODUCTS_INGESTED: BadgeVariant.SUCCESS,
  PRODUCTS_VERIFIED: BadgeVariant.SUCCESS,
  PRODUCTS_INGESTED: BadgeVariant.SUCCESS,
  PROCESSED: BadgeVariant.SUCCESS,
  BUSINESS_PARTNER_VERIFIED: BadgeVariant.SUCCESS,
  BUSINESS_PARTNER_INGESTED: BadgeVariant.SUCCESS,
  ECO_MODULATION_INGESTED: BadgeVariant.SUCCESS,
} as const satisfies Record<
  FindIngestionsResponseDtoStatusEnumKey,
  BadgeVariant
>

export function getIngestionStatusVariant(
  status: FindIngestionsResponseDtoStatusEnumKey
): BadgeVariant {
  return IngestionStatusVariantEnum[status]
}

function getIngestionBadgeIcon(status: FindIngestionsResponseDtoStatusEnumKey) {
  const statusIconMap = {
    PENDING: <IconHourglass />,
    ERROR: <IconAlertTriangle />,
    FAILED: <IconAlertTriangle />,
  } as const

  if (
    status === findIngestionsResponseDtoStatusEnum.PENDING ||
    status === findIngestionsResponseDtoStatusEnum.ERROR ||
    status === findIngestionsResponseDtoStatusEnum.FAILED
  ) {
    return statusIconMap[status]
  }

  return <IconChecks />
}

function getIngestionStatusDisplay(
  status: FindIngestionsResponseDtoStatusEnumKey,
  translationMap: Record<"PENDING" | "ERROR" | "FAILED" | "SUCCESS", string>
): string {
  if (status === findIngestionsResponseDtoStatusEnum.PENDING) {
    return translationMap.PENDING
  }

  if (status === findIngestionsResponseDtoStatusEnum.ERROR) {
    return translationMap.ERROR
  }

  if (status === findIngestionsResponseDtoStatusEnum.FAILED) {
    return translationMap.FAILED
  }

  return translationMap.SUCCESS
}

export function IngestionStatusBadge({
  status,
}: {
  status: FindIngestionsResponseDtoStatusEnumKey
}) {
  const { t } = useTranslation("dataTransfer")
  const variant = getIngestionStatusVariant(status)
  const icon = getIngestionBadgeIcon(status)
  const displayStatus = getIngestionStatusDisplay(status, {
    PENDING: t("import.list.table.columns.status.cell.pending"),
    ERROR: t("import.list.table.columns.status.cell.error"),
    FAILED: t("import.list.table.columns.status.cell.failed"),
    SUCCESS: t("import.list.table.columns.status.cell.success"),
  })

  return <StatusBadge text={displayStatus} icon={icon} variant={variant} />
}
